import {
  Form,
  Input,
  Button,
  theme,
  App,
  message,
  Divider,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setAdditionalSharing,
  setAuth,
  setUserDetail,
  setUserIP,
} from "../../store/slice/authSlice";
import APICall from "../../APICall";
import EndPoints from "../../APICall/EndPoints";
import { resetWebSocket } from "../../store/slice/webSocketSlice";
import { resetMarketWatchSlice } from "../../store/slice/marketWatchSlice";
import { resetUserAccountSlice } from "../../store/slice/userAccountSlice";
import axios from "axios";
import {
  marketDataList,
  resetNotification,
  setApiLink,
  setFilterUsers,
} from "../../store/slice/globleSlice";
import { useQueryClient } from "@tanstack/react-query";
import LogoImage from "../../Image/Bison_Capital.png";
import {
  AndroidFilled,
  AppleFilled,
  DownloadOutlined,
  UserOutlined,
  LockOutlined
} from "@ant-design/icons";
import { useAuthChannel } from "../../hooks/useAuthChannel";
import { sequence } from "../../Helper/constant";
import { getApplicationLink } from "../../components/applicationLink";
import {
  LoginContainer,
  LeftPanel,
  RightPanel,
  FormWrapper,
  StyledForm,
  WelcomeTitle,
  WelcomeSubText,
  BrandLogo,
  MobileLogo,
  LoginTitle,
  LoginSubtitle,
  ActionButtons,
  DownloadButton,
  InstallButton
} from "./Login.styles";

const Login = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notification } = App.useApp();
  const { broadcastLogin, broadcastLogout } = useAuthChannel();
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();

  //state
  const [isLoading, setIsLoading] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  //redux
  const userIP = useSelector((state) => state.authSlice?.user?.userIP);
  const apiLink = useSelector((state) => state?.globleSlice?.apiLink);

  //style
  const token = theme.useToken().token;

  //lifecycle
  useEffect(() => {
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    });
  }, []);
  
  useEffect(() => {
    dispatch(setAuth(null));
    dispatch(resetWebSocket());
    dispatch(resetNotification());
    dispatch(resetMarketWatchSlice({ key: "all" }));
    dispatch(resetUserAccountSlice());
    getClientIP();
    queryClient.clear();
    getApplicationLink().then((data) => {
      dispatch(setApiLink(data));
    });
  }, []);

  //functions
  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === "accepted") {
          console.log("User accepted the install prompt");
        } else {
          console.log("User dismissed the install prompt");
        }
        setDeferredPrompt(null);
      });
    }
  };

  const getClientIP = async () => {
    try {
      const response = await axios("https://api64.ipify.org?format=json");
      if (response.status === 200 && response.data) {
        const data = response.data;
        dispatch(setUserIP(data?.ip));
      }
    } catch (error) {
      console.error("Error fetching IP address:", error);
    }
  };

  const getMarketList = async (marketList) => {
    const sortedArray = marketList
      .sort((a, b) => {
        const indexA = sequence.indexOf(a.scriptName);
        const indexB = sequence.indexOf(b.scriptName);

        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      })
      .map((item) => ({
        ...item,
        label: item.scriptName,
        value: item.scriptName,
      }));
    dispatch(marketDataList(sortedArray));
  };

  const handleLogin = async (values) => {
    const newValues = { ...values, loginType: "WEB", ipAddress: userIP };
    if (!isLoading) {
      form
        .validateFields()
        .then(async () => {
          setIsLoading(true);
          await APICall("post", EndPoints.login, newValues).then((response) => {
            let res = response?.data;
            if (response?.status === 200 && res) {
              broadcastLogout();
              let data = response?.data?.data;
              queryClient.resetQueries();
              getMarketList(data?.marketList);
              dispatch(setAuth(data?.user?.token));
              dispatch(
                setUserDetail({ ...data?.user, ...data?.partnerShipWith })
              );
              dispatch(
                setAdditionalSharing({
                  ...data?.additionalSharing,
                  accountType: data?.user?.accountType,
                })
              );
              dispatch(setFilterUsers([data?.user?.userId]));
              localStorage.setItem("token", data?.user?.token);
              broadcastLogin(data?.user?.token);
              getClientIP();
              if (data?.user?.resetPassword === true) {
                navigate("/resetPassward");
              } else {
                navigate("/dashboard");
              }

              notification?.success({
                message: res?.message,
              });
            } else {
              if (res?.message) {
                messageApi.open({
                  type: "error",
                  content: `${res?.message}`,
                  duration: 3,
                });
                form.setFieldsValue({ password: "" });
                setTimeout(() => {
                  form.getFieldInstance("password")?.focus();
                }, 100);
              }
            }
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <LoginContainer>
      {contextHolder}
      
      <LeftPanel>
        <BrandLogo src={LogoImage} alt="Logo" />
        <WelcomeTitle>Welcome Back</WelcomeTitle>
        <WelcomeSubText>
          Access your professional virtual trading dashboard and stay ahead of the market trends.
        </WelcomeSubText>
      </LeftPanel>

      <RightPanel>
        <FormWrapper>
          <MobileLogo src={LogoImage} alt="Logo" />
          <LoginTitle>Sign In</LoginTitle>
          <LoginSubtitle>Enter your User ID and password to access your account.</LoginSubtitle>
          
          <StyledForm
            form={form}
            onFinish={handleLogin}
            name="login-form"
            layout="vertical"
            initialValues={{
              remember: true,
              userId: "",
              password: "",
              loginType: "WEB",
            }}
          >
            <Form.Item
              name="userId"
              rules={[{ required: true, message: "Please input your User Id" }]}
            >
              <Input 
                prefix={<UserOutlined style={{ color: token.colorTextPlaceholder }} />} 
                placeholder="User ID" 
                size="large" 
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: "Please input your password!" }]}
            >
              <Input.Password 
                prefix={<LockOutlined style={{ color: token.colorTextPlaceholder }} />} 
                placeholder="Password" 
                size="large" 
              />
            </Form.Item>

            <Form.Item>
              <Button
                block
                type="primary"
                htmlType="submit"
                size="large"
                loading={isLoading}
                disabled={isLoading}
                style={{ height: '48px', fontWeight: '500', fontSize: '1rem', marginTop: '1rem' }}
              >
                Login
              </Button>
            </Form.Item>
          </StyledForm>

          <Divider plain>Download Apps</Divider>
          
          <ActionButtons>
            <DownloadButton href={apiLink?.android} target="_blank">
              <AndroidFilled className="icon" />
              <span className="text">Android APK</span>
            </DownloadButton>
            
            <DownloadButton href={apiLink?.apple} target="_blank">
              <AppleFilled className="icon" />
              <span className="text">Apple App</span>
            </DownloadButton>

            {deferredPrompt && (
              <InstallButton onClick={handleInstallClick} disabled={!deferredPrompt}>
                <DownloadOutlined className="icon" />
                <span className="text">Install Web App</span>
              </InstallButton>
            )}
          </ActionButtons>

        </FormWrapper>
      </RightPanel>
    </LoginContainer>
  );
};

export default Login;
